import type { Route } from "./+types/login.ts"

import * as UI from "@iyk/ui"

import { Email } from "@iyk/core"
import { OAuth, User } from "@iyk/vf-core"
import { FormState, Outcome, useForm } from "@iyk/vf-forms"
import { Req, Res } from "@iyk/vf-server"
import { PageContainer, Terms } from "@iyk/vf-ui"
import { Data, Effect, Schema } from "effect"
import { appConfig } from "../../app-config.ts"
import { GlobalRoutes } from "../../react-router/routes.ts"
import { useLang } from "../../react-router/use-lang.ts"
import { Handler } from "../../server/handler.ts"
import { FanLayout } from "../../ui/fan-layout.tsx"
import { HeaderLogo } from "../../ui/header-logo.tsx"

export const meta: Route.MetaFunction = () => [
  { title: `Login | ${appConfig.artist.name}` },
  { property: "og:title", content: `Login | ${appConfig.artist.name}` }
]

export const loader = Handler.make("auth-login", ({ cookies, sessions, cookieJar }) => {
  return Effect.gen(function* () {
    const returnTo = yield* Req.parseSearchParams(Schema.Struct({ returnTo: User.Auth.ReturnTo })).pipe(
      Effect.map(({ returnTo }) => returnTo),
      Effect.orElseSucceed(() => User.Auth.ReturnTo.unsafeFrom(GlobalRoutes.profile.home)),
      Effect.tap((returnTo) => cookieJar.set(cookies.loginIntent, returnTo))
    )

    return yield* sessions.authenticated.pipe(
      Effect.match({
        onSuccess: () => Res.redirect(GlobalRoutes.profile.home),
        onFailure: () => Res.json({ returnTo })
      })
    )
  })
})

export const action = Handler.make("auth-login", ({ users, cookies, cookieJar }) =>
  Req.parseFormData(LoginFormSchema).pipe(
    Effect.flatMap(
      matchLoginForm({
        OAuth: (body) =>
          Effect.gen(function* () {
            const provider = OAuth.Provider[body.provider]()
            const url = yield* users.createOAuthUrl(provider, body.returnTo)

            return Res.redirect(url.href)
          }),
        Otp: (body) =>
          Effect.gen(function* () {
            yield* users.authenticateWithOtp(body.email)

            yield* cookieJar.set(cookies.emailFromOtp, body.email)

            return Res.redirect(GlobalRoutes.verify)
          })
      })
    ),
    Effect.mapError(({ errors }) => Res.json(Outcome.Failure(errors))),
    Effect.merge
  )
)

export default function AuthLoginRoute(props: Route.ComponentProps) {
  const { returnTo } = props.loaderData
  const { Form, errors, state } = useForm<typeof action>()
  const lang = useLang()

  const isDisabled = FormState.$match(state, {
    Submitting: () => true,
    Failed: () => false,
    Idle: () => false,
    Succeeded: () => false
  })

  return (
    <FanLayout>
      <PageContainer>
        <HeaderLogo />
        <div className="grow flex flex-col gap-4 items-center justify-center text-center">
          <p className="text-lg leading-6 max-w-48">Log in or sign up</p>
        </div>
        <div className="px-4 pb-6 flex flex-col gap-3 overflow-hidden">
          <Form method="post" className="flex flex-col gap-3">
            <input type="hidden" name="returnTo" value={returnTo} />
            <input type="hidden" name="_tag" value="OAuth" />

            <UI.Button
              type="submit"
              name="provider"
              value="Google"
              kind="outline"
              size="md"
              className="w-full"
              disabled={isDisabled}
            >
              <UI.Icon.Google />
              Continue with Google
            </UI.Button>
          </Form>

          <div className="flex items-center gap-2">
            <hr className="flex-1 text-gray-4" />
            <span className="text-sm text-gray-9">or</span>
            <hr className="flex-1 text-gray-4" />
          </div>

          <Form method="post" className="flex flex-col gap-3 mt-3">
            <input type="hidden" name="returnTo" value={returnTo} />
            <input type="hidden" name="_tag" value="Otp" />

            <UI.Field name="email" required validationMessage={errors?.email}>
              <UI.Textbox type="email" autoComplete="email" placeholder="Email" />
              <UI.Validity />
            </UI.Field>

            <UI.Button
              type="submit"
              kind="solid"
              size="lg"
              className="w-full items-center"
              disabled={isDisabled}
            >
              Continue with email
            </UI.Button>

            <Terms
              privacyPolicyUrl={appConfig.assets.documents.privacyPolicy}
              termsOfServiceUrl={appConfig.assets.documents.termsOfService}
              lang={lang}
            />
          </Form>
        </div>
      </PageContainer>
    </FanLayout>
  )
}

const EmailForm = Schema.Struct({
  _tag: Schema.Literal("Otp"),
  email: Email.annotations({ message: () => "Please enter a valid email" }),
  returnTo: User.Auth.ReturnTo
})

const OAuthForm = Schema.Struct({
  _tag: Schema.Literal("OAuth"),
  provider: OAuth.Provider.schema.fields._tag,
  returnTo: User.Auth.ReturnTo
})

type LoginFormSchema = typeof LoginFormSchema.Type
const LoginFormSchema = Schema.Union(EmailForm, OAuthForm)
const { $match: matchLoginForm } = Data.taggedEnum<LoginFormSchema>()
